import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import * as FiIcons from 'react-icons/fi'
import SafeIcon from '../../common/SafeIcon'
import { format } from 'date-fns'
import { supabase } from '../../lib/supabase'
import Toast from '../ui/Toast'

const { FiPlus, FiCheck, FiX, FiZap, FiList } = FiIcons

const QuickMenuSelector = ({ onClose, targetDate, onSuccess }) => {
  const [presetMenus, setPresetMenus] = useState({ primero: [], segundo: [] })
  const [selectedItems, setSelectedItems] = useState({ primero: [], segundo: [] })
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [toast, setToast] = useState(null)

  useEffect(() => {
    fetchPresetMenus()
  }, [])

  const showToast = (message, type = 'success') => {
    setToast({ message, type })
    setTimeout(() => setToast(null), 3000)
  }

  const fetchPresetMenus = async () => {
    try {
      setLoading(true)
      const { data, error } = await supabase
        .from('menu_presets_3k8m9x')
        .select('*')
        .in('categoria', ['primero', 'segundo'])
        .order('categoria')
        .order('orden')

      if (error) {
        console.error('Error fetching preset menus:', error)
        showToast('Error al cargar los menús predefinidos', 'error')
        return
      }

      const grouped = data.reduce((acc, item) => {
        if (!acc[item.categoria]) {
          acc[item.categoria] = []
        }
        acc[item.categoria].push(item)
        return acc
      }, { primero: [], segundo: [] })

      setPresetMenus(grouped)
    } catch (error) {
      console.error('Error fetching preset menus:', error)
      showToast('Error al cargar los menús predefinidos', 'error')
    } finally {
      setLoading(false)
    }
  }

  const toggleItemSelection = (categoria, item) => {
    setSelectedItems(prev => ({
      ...prev,
      [categoria]: prev[categoria].some(selected => selected.id === item.id)
        ? prev[categoria].filter(selected => selected.id !== item.id)
        : [...prev[categoria], item]
    }))
  }

  const handleSaveSelection = async () => {
    try {
      setSaving(true)
      const allSelectedItems = [...selectedItems.primero, ...selectedItems.segundo]

      if (allSelectedItems.length === 0) {
        showToast('Selecciona al menos un plato para continuar', 'error')
        return
      }

      // Prepare data for insertion - Con descripción vacía por defecto
      const platosToInsert = allSelectedItems.map((item, index) => ({
        fecha: targetDate,
        nombre: item.nombre,
        categoria: item.categoria,
        descripcion: '', // Descripción vacía por defecto
        alergenos: [],
        precio: null, // Menu items don't have individual prices
        visible: true,
        orden: item.orden || index
      }))

      // Insert the selected items
      const { error } = await supabase
        .from('platos')
        .insert(platosToInsert)

      if (error) {
        console.error('Error inserting menu items:', error)
        showToast('Error al guardar los platos: ' + error.message, 'error')
        return
      }

      showToast(`Se han añadido ${allSelectedItems.length} platos al menú`)
      onSuccess()
      onClose()
    } catch (error) {
      console.error('Error saving menu selection:', error)
      showToast('Error al guardar la selección', 'error')
    } finally {
      setSaving(false)
    }
  }

  const selectAllInCategory = (categoria) => {
    setSelectedItems(prev => ({
      ...prev,
      [categoria]: presetMenus[categoria]
    }))
  }

  const clearCategory = (categoria) => {
    setSelectedItems(prev => ({
      ...prev,
      [categoria]: []
    }))
  }

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-lg p-8">
          <div className="flex justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-vip-orange"></div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      {/* Toast */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden"
      >
        <div className="p-6 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-xl font-bold text-vip-orange flex items-center">
                <SafeIcon icon={FiZap} className="mr-2" />
                Selector Rápido de Menú
              </h2>
              <p className="text-vip-brown">
                Fecha: {format(new Date(targetDate), 'dd/MM/yyyy')}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
            >
              <SafeIcon icon={FiX} className="h-6 w-6" />
            </button>
          </div>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
          <div className="grid md:grid-cols-2 gap-8">
            {/* Primeros */}
            <div>
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-vip-brown flex items-center">
                  🥣 Primeros ({selectedItems.primero.length}/{presetMenus.primero.length})
                </h3>
                <div className="flex gap-2">
                  <button
                    onClick={() => selectAllInCategory('primero')}
                    className="text-xs px-2 py-1 bg-vip-orange text-white rounded hover:bg-vip-dark transition-colors duration-200"
                  >
                    Todos
                  </button>
                  <button
                    onClick={() => clearCategory('primero')}
                    className="text-xs px-2 py-1 bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors duration-200"
                  >
                    Limpiar
                  </button>
                </div>
              </div>
              
              <div className="space-y-2">
                {presetMenus.primero.map((item) => {
                  const isSelected = selectedItems.primero.some(selected => selected.id === item.id)
                  return (
                    <motion.button
                      key={item.id}
                      onClick={() => toggleItemSelection('primero', item)}
                      className={`w-full text-left p-3 rounded-lg border transition-all duration-200 ${
                        isSelected
                          ? 'bg-vip-orange text-white border-vip-orange'
                          : 'bg-gray-50 hover:bg-gray-100 border-gray-200'
                      }`}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <span className="text-lg mr-2">{item.emoji}</span>
                          <span className="font-medium">{item.nombre}</span>
                        </div>
                        {isSelected && (
                          <SafeIcon icon={FiCheck} className="text-white" />
                        )}
                      </div>
                    </motion.button>
                  )
                })}
              </div>
            </div>

            {/* Segundos */}
            <div>
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-vip-brown flex items-center">
                  🍽️ Segundos ({selectedItems.segundo.length}/{presetMenus.segundo.length})
                </h3>
                <div className="flex gap-2">
                  <button
                    onClick={() => selectAllInCategory('segundo')}
                    className="text-xs px-2 py-1 bg-vip-orange text-white rounded hover:bg-vip-dark transition-colors duration-200"
                  >
                    Todos
                  </button>
                  <button
                    onClick={() => clearCategory('segundo')}
                    className="text-xs px-2 py-1 bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors duration-200"
                  >
                    Limpiar
                  </button>
                </div>
              </div>
              
              <div className="space-y-2">
                {presetMenus.segundo.map((item) => {
                  const isSelected = selectedItems.segundo.some(selected => selected.id === item.id)
                  return (
                    <motion.button
                      key={item.id}
                      onClick={() => toggleItemSelection('segundo', item)}
                      className={`w-full text-left p-3 rounded-lg border transition-all duration-200 ${
                        isSelected
                          ? 'bg-vip-orange text-white border-vip-orange'
                          : 'bg-gray-50 hover:bg-gray-100 border-gray-200'
                      }`}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <span className="text-lg mr-2">{item.emoji}</span>
                          <span className="font-medium">{item.nombre}</span>
                        </div>
                        {isSelected && (
                          <SafeIcon icon={FiCheck} className="text-white" />
                        )}
                      </div>
                    </motion.button>
                  )
                })}
              </div>
            </div>
          </div>

          {/* Selection Summary */}
          {(selectedItems.primero.length > 0 || selectedItems.segundo.length > 0) && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg"
            >
              <h4 className="font-semibold text-blue-900 mb-2">Resumen de Selección:</h4>
              <div className="text-sm text-blue-800">
                <p>• Primeros seleccionados: {selectedItems.primero.length}</p>
                <p>• Segundos seleccionados: {selectedItems.segundo.length}</p>
                <p className="mt-2 font-medium">
                  Total: {selectedItems.primero.length + selectedItems.segundo.length} platos
                </p>
              </div>
            </motion.div>
          )}
        </div>

        <div className="p-6 border-t border-gray-200 flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 transition-colors duration-200"
          >
            Cancelar
          </button>
          <button
            onClick={handleSaveSelection}
            disabled={saving || (selectedItems.primero.length === 0 && selectedItems.segundo.length === 0)}
            className="flex items-center px-6 py-2 bg-vip-orange text-white rounded-md hover:bg-vip-dark transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Guardando...
              </>
            ) : (
              <>
                <SafeIcon icon={FiPlus} className="mr-2" />
                Añadir ({selectedItems.primero.length + selectedItems.segundo.length})
              </>
            )}
          </button>
        </div>
      </motion.div>
    </div>
  )
}

export default QuickMenuSelector